/** Reads Firebase's stable Storage error code without assuming every thrown value is an Error. */
export function storageErrorCode(error: unknown): string | undefined {
    if (!error || typeof error !== 'object' || !('code' in error)) {
        return undefined;
    }

    const code = (error as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
}

/** Reads the HTTP status Firebase attaches to Storage errors. A status of 0 means no response arrived. */
export function storageHttpStatus(error: unknown): number | undefined {
    if (!error || typeof error !== 'object' || !('status' in error)) {
        return undefined;
    }

    const status = (error as { status?: unknown }).status;
    return typeof status === 'number' && Number.isFinite(status) ? status : undefined;
}

/**
 * Whether starting a fresh resumable session can reasonably recover this failure.
 *
 * Firebase does not retry failed resumable-upload chunks. In particular, a connection loss is surfaced
 * as `storage/unknown` with HTTP status 0, even while `navigator.onLine` remains true on mobile devices.
 */
export function isRetryableStorageError(error: unknown): boolean {
    const code = storageErrorCode(error);
    const status = storageHttpStatus(error);

    if (code === 'storage/retry-limit-exceeded') {
        return true;
    }

    if (code !== 'storage/unknown') {
        return false;
    }

    return (
        status === undefined ||
        status === 0 ||
        // A resumable chunk that only partially persisted. The SDK advances its own offset by the length
        // it *intended* to send rather than by the count the server reports, so the next request declares
        // an `X-Goog-Upload-Offset` the session does not agree with and Storage rejects it with 400.
        // Nothing about the file is wrong; a fresh session restarts from zero and resolves the mismatch.
        // Observed in production on iOS Safari, always at the first 256 KiB chunk boundary.
        status === 400 ||
        status === 404 ||
        status === 408 ||
        status === 410 ||
        status === 425 ||
        status === 429 ||
        status >= 500
    );
}
